<template>
  <div class="space-y-6">
    <!-- Header / Intro -->
    <div>
      <h2 class="text-xl font-semibold text-gray-900">Triggers & Actions</h2>
      <p class="mt-1 text-sm text-gray-500">
        Configure automation rules that run after AI conversations. A
        <span class="font-medium">Trigger</span> is an outcome like
        <em>Qualified</em> or <em>Requested Callback</em>. An
        <span class="font-medium">Action</span> is what we do next: send an
        email, POST a webhook, or auto-tag the call. You can reference variables
        in emails like <code>[customer_phone_number]</code>,
        <code>[transcription]</code>, <code>[recording_url]</code>,
        <code>[lead_type]</code>.
      </p>
    </div>

    <!-- Add Button -->
    <div class="flex items-center justify-between">
      <button
        @click="openCreate"
        class="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow hover:bg-indigo-500 focus:outline-none"
      >
        + New Trigger / Action
      </button>
    </div>

    <!-- Empty State -->
    <div
      v-if="!loading && automations.length === 0"
      class="rounded-lg border border-dashed border-gray-300 p-8 text-center"
    >
      <p class="text-gray-600">No automations yet.</p>
      <p class="mt-1 text-sm text-gray-500">
        Create your first rule to automatically notify your team, call webhooks,
        or tag calls based on outcomes.
      </p>
      <button
        @click="openCreate"
        class="mt-4 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"
      >
        Create Rule
      </button>
    </div>

    <!-- List -->
    <div v-if="automations.length" class="grid grid-cols-1 gap-4">
      <div
        v-for="item in automations"
        :key="item.id"
        class="rounded-lg border bg-white p-4 shadow-sm"
      >
        <div class="flex items-start justify-between">
          <div>
            <h3 class="text-base font-semibold text-gray-900">
              {{ item.title }}
            </h3>
            <p class="mt-1 text-xs text-gray-500">
              Trigger: {{ humanTrigger(item.trigger_type) }} • Action:
              {{ humanAction(item.action_type) }}
            </p>
          </div>
          <div class="flex gap-2">
            <button
              @click="edit(item)"
              class="rounded-md border px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Edit
            </button>
            <button
              @click="confirmDelete(item)"
              class="rounded-md border px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
            >
              Delete
            </button>
          </div>
        </div>

        <div class="mt-3 text-sm text-gray-600" v-if="item.instructions">
          <span class="font-medium">AI check:</span> {{ item.instructions }}
        </div>

        <div
          class="mt-3 grid gap-2 text-sm"
          v-if="item.action_type === 'email'"
        >
          <div>
            <span class="font-medium">Email Subject:</span>
            {{ item.email_subject }}
          </div>
          <div>
            <span class="font-medium">Email Body:</span>
            <pre class="whitespace-pre-wrap rounded bg-gray-50 p-2">{{
              item.email_body
            }}</pre>
          </div>
        </div>

        <div
          class="mt-3 text-sm"
          v-else-if="item.action_type === 'webhook'"
        >
          <span class="font-medium">Webhook URL:</span>
          <span class="text-gray-700">{{ item.webhook_url }}</span>
        </div>

        <div
          class="mt-3 text-sm"
          v-else-if="item.action_type === 'autotag'"
        >
          <span class="font-medium">Apply Tags:</span>
          <span class="text-gray-700">{{
            (item.tag_ids || []).map(tagName).join(', ') || '—'
          }}</span>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
    >
      <div class="w-full max-w-2xl rounded-lg bg-white shadow-xl">
        <div class="flex items-center justify-between border-b p-4">
          <h3 class="text-lg font-semibold">
            {{ isEditing ? 'Edit Trigger / Action' : 'New Trigger / Action' }}
          </h3>
          <button
            @click="closeModal"
            class="rounded p-1 text-gray-500 hover:bg-gray-100"
          >
            ✕
          </button>
        </div>

        <div class="p-4 space-y-4">
          <!-- Title -->
          <div>
            <label class="block text-sm font-medium text-gray-700">Title</label>
            <input
              v-model.trim="form.title"
              type="text"
              class="mt-1 w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="e.g. Email sales when lead is qualified"
            />
          </div>

          <!-- Instructions -->
          <div>
            <label class="block text-sm font-medium text-gray-700"
              >AI Instructions</label
            >
            <textarea
              v-model.trim="form.instructions"
              rows="2"
              class="mt-1 w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Did the customer fit the customer target definition, like specified in our main prompt?"
            ></textarea>
          </div>

          <!-- Trigger + Action -->
          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <label class="block text-sm font-medium text-gray-700"
                >Trigger</label
              >
              <select
                v-model="form.trigger_type"
                @change="maybeGenerateEmailDefaults()"
                class="mt-1 w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option disabled value="">Select a trigger…</option>
                <option value="qualified">Qualified</option>
                <option value="lead_qualified_with_data">
                  Lead Qualified with Data
                </option>
                <option value="requested_callback">Requested Callback</option>
                <option value="speak_to_representative">
                  Speak to a Representative
                </option>
                <option value="change_appointment">Change Appointment</option>
                <option value="schedule_appointment">Schedule Appointment</option>
                <option value="emergency_callback">Emergency Callback</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700"
                >Action</label
              >
              <select
                v-model="form.action_type"
                @change="maybeGenerateEmailDefaults()"
                class="mt-1 w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option disabled value="">Select an action…</option>
                <option value="email">Send Email</option>
                <option value="webhook">Send Webhook</option>
                <option value="autotag">Auto-Tag Call</option>
              </select>
            </div>
          </div>

          <!-- Action configs -->
          <div v-if="form.action_type === 'email'" class="space-y-3">
            <div class="rounded-md bg-gray-50 p-3 text-xs text-gray-600">
              Use variables: <code>[customer_phone_number]</code>,
              <code>[transcription]</code>, <code>[recording_url]</code>,
              <code>[lead_type]</code>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700"
                >Email Subject</label
              >
              <input
                v-model.trim="form.email_subject"
                type="text"
                class="mt-1 w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="e.g. New qualified lead: [customer_phone_number]"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700"
                >Email Body</label
              >
              <textarea
                v-model="form.email_body"
                rows="6"
                class="mt-1 w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
              ></textarea>
            </div>
          </div>

          <div v-else-if="form.action_type === 'webhook'" class="space-y-3">
            <div>
              <label class="block text-sm font-medium text-gray-700"
                >Webhook URL</label
              >
              <input
                v-model.trim="form.webhook_url"
                type="url"
                class="mt-1 w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="https://yourapp.com/webhooks/diyai"
              />
            </div>
          </div>

          <div v-else-if="form.action_type === 'autotag'" class="space-y-3">
            <label class="block text-sm font-medium text-gray-700"
              >Select Tags</label
            >
            <div class="mt-1 flex flex-wrap gap-2">
              <label
                v-for="tag in tags"
                :key="tag.id"
                class="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-sm"
              >
                <input type="checkbox" :value="tag.id" v-model="form.tag_ids" />
                <span>{{ tag.name }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="flex justify-end gap-2 border-t p-4">
          <button @click="closeModal" class="rounded-md border px-3 py-2 text-sm">
            Cancel
          </button>
          <button
            @click="save"
            :disabled="saving"
            class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {{ saving ? 'Saving…' : isEditing ? 'Save Changes' : 'Create' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirm -->
    <div
      v-if="deletingItem"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
    >
      <div class="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div class="p-5">
          <h3 class="text-base font-semibold text-gray-900">
            Delete this rule?
          </h3>
          <p class="mt-1 text-sm text-gray-600">This cannot be undone.</p>
        </div>
        <div class="flex justify-end gap-2 border-t p-4">
          <button @click="deletingItem = null" class="rounded-md border px-3 py-2 text-sm">
            Cancel
          </button>
          <button
            @click="destroy"
            class="rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-500"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "TriggerActionsManager",
  data() {
    return {
      loading: false,
      saving: false,
      automations: [],
      tags: [
      	{
      		id: 1,
      		name: "Missed Call"
      	},
      	{
      		id: 2,
      		name: "Became Customer"
      	},
      	{
      		id: 3,
      		name: "Complete"
      	},
      	{
      		id: 4,
      		name: "Incomplete"
      	},
      	{
      		id: 5,
      		name: "AI Test Call"
      	},
      	{
      		id: 6,
      		name: "Duplicate Call"
      	},
      	{
      		id: 7,
      		name: "Qualified"
      	},
      	{
      		id: 8,
      		name: "Unqualified"
      	},
      ],
      showModal: false,
      isEditing: false,
      editingId: null,
      deletingItem: null,
      form: this.emptyForm(),
    };
  },
  mounted() {
    this.fetchAll();
  },
  methods: {
    emptyForm() {
      return {
        title: "",
        instructions: "",
        trigger_type: "",
        action_type: "",
        email_subject: "",
        email_body: "",
        webhook_url: "",
        tag_ids: [],
      };
    },
    async fetchAll() {
      this.loading = true;
      try {
        const [a] = await Promise.all([
          axios.get(`${process.env.VUE_APP_API_ENDPOINT}/automations`, {
	          headers: {
	            'Authorization': 'Bearer ' + this.$store.state.auth.jwt
	          }
	        })
        ]);
        this.automations = a.data;
      } finally {
        this.loading = false;
      }
    },
    humanTrigger(v) {
      const map = {
        qualified: "Qualified",
        lead_qualified_with_data: "Lead Qualified with Data",
        requested_callback: "Requested Callback",
        speak_to_representative: "Speak to a Representative",
        change_appointment: "Change Appointment",
        schedule_appointment: "Schedule Appointment",
        emergency_callback: "Emergency Callback",
      };
      return map[v] || v;
    },
    humanAction(v) {
      return { email: "Email", webhook: "Webhook", autotag: "Auto-Tag" }[v] || v;
    },
    tagName(id) {
      const t = this.tags.find((x) => x.id === id);
      return t ? t.name : `#${id}`;
    },
    openCreate() {
      this.isEditing = false;
      this.editingId = null;
      this.form = this.emptyForm();
      this.showModal = true;
    },
    edit(item) {
      this.isEditing = true;
      this.editingId = item.id;
      this.form = { ...item, tag_ids: item.tag_ids || [] };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async save() {
      this.saving = true;
      try {
        const payload = { ...this.form };
        let res;
        if (this.isEditing) {
          res = await axios.put(`${process.env.VUE_APP_API_ENDPOINT}/automations/${this.editingId}`, payload, {
	          headers: {
	            'Authorization': 'Bearer ' + this.$store.state.auth.jwt
	          }
	        });
        } else {
          res = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/automations`, payload, {
	          headers: {
	            'Authorization': 'Bearer ' + this.$store.state.auth.jwt
	          }
	        });
        }
        if (this.isEditing) {
          const ix = this.automations.findIndex((a) => a.id === this.editingId);
          if (ix !== -1) this.automations.splice(ix, 1, res.data);
        } else {
          this.automations.unshift(res.data);
        }
        this.closeModal();
      } catch (e) {
        alert("Unable to save. Please check inputs.");
      } finally {
        this.saving = false;
      }
    },

    maybeGenerateEmailDefaults() {
	  if (this.form.action_type !== 'email') return

	  const defaults = {
	    qualified: {
	      subject: 'New qualified lead: [customer_phone_number]',
	      body: `A new lead is qualified.

	Lead type: [lead_type]
	Phone: [customer_phone_number]
	Recording: [recording_url]

	Transcript:
	[transcription]`
	    },
	    lead_qualified_with_data: {
	      subject: 'Qualified lead (data complete): [customer_phone_number]',
	      body: `A lead is qualified and all required data was collected.

	Lead type: [lead_type]
	Phone: [customer_phone_number]
	Recording: [recording_url]

	Transcript:
	[transcription]`
	    },
	    requested_callback: {
	      subject: 'Callback requested: [customer_phone_number]',
	      body: `The customer requested a callback.

	Phone: [customer_phone_number]
	Recording: [recording_url]

	Transcript:
	[transcription]`
	    },
	    speak_to_representative: {
	      subject: 'Representative requested: [customer_phone_number]',
	      body: `The caller asked to speak to a representative.

	Phone: [customer_phone_number]
	Recording: [recording_url]

	Transcript:
	[transcription]`
	    },
	    change_appointment: {
	      subject: 'Appointment change requested: [customer_phone_number]',
	      body: `The caller requested to change an appointment.

	Phone: [customer_phone_number]
	Recording: [recording_url]

	Transcript:
	[transcription]`
	    },
	    schedule_appointment: {
	      subject: 'Appointment scheduling requested: [customer_phone_number]',
	      body: `The caller wants to schedule an appointment.

	Phone: [customer_phone_number]
	Recording: [recording_url]

	Transcript:
	[transcription]`
	    },
	    emergency_callback: {
	      subject: 'Emergency callback: [customer_phone_number]',
	      body: `The caller indicated an emergency and requested a callback.

	Phone: [customer_phone_number]
	Recording: [recording_url]

	Transcript:
	[transcription]`
	    }
	  }

	  const trigger = this.form.trigger_type

	  if (trigger && defaults[trigger]) {
	      this.form.email_subject = defaults[trigger].subject
	      this.form.email_body = defaults[trigger].body
	  }

	  if (trigger && !defaults[trigger]) {
	      this.form.email_subject = ''
	      this.form.email_body = ''
	  }
	},

    confirmDelete(item) {
      this.deletingItem = item;
    },
    async destroy() {
      await axios.delete(`${process.env.VUE_APP_API_ENDPOINT}/automations/${this.deletingItem.id}`, {
          headers: {
            'Authorization': 'Bearer ' + this.$store.state.auth.jwt
          }
      });
      this.automations = this.automations.filter(
        (a) => a.id !== this.deletingItem.id
      );
      this.deletingItem = null;
    },
  },
};
</script>

<style type="text/css" scoped>
	input[type="text"], input[type="url"], textarea, select {
		border: 1px solid rgba(25, 35, 58, 0.3) !important;
		font-size: 12px;
		padding: 8px;
	}
</style>